import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Download, Search, Bell, CheckCircle, FileText, Calendar } from 'lucide-react';
import type { Database } from '../../lib/database.types';
import { useTheme } from '../../contexts/ThemeContext';
import { getTranslation } from '../../lib/translations';

type Receipt = Database['public']['Tables']['receipts']['Row'] & {
  categories?: Database['public']['Tables']['categories']['Row'];
};

interface TaxReportsProps {
  devModeUserId?: string | null;
}

export default function TaxReports({ devModeUserId }: TaxReportsProps = {}) {
  const { language } = useTheme();
  const t = (key: string) => getTranslation(language, key);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState<{
    total: number;
    deductible: number;
    taxAmount: number;
    eligibleRecords: number;
    byCategory: Record<string, { amount: number; count: number; deductible: boolean }>;
  } | null>(null);

  const currentYear = new Date().getFullYear();
  const gstRate = 0.09;
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchReceipts();
  }, [devModeUserId, selectedYear]);

  async function fetchReceipts() {
    try {
      setLoading(true);
      const yearStart = `${selectedYear}-01-01`;
      const yearEnd = `${selectedYear}-12-31`;

      let query = supabase
        .from('receipts')
        .select('*, categories(*)')
        .eq('status', 'parsed')
        .gte('receipt_date', yearStart)
        .lte('receipt_date', yearEnd)
        .order('receipt_date', { ascending: true });

      if (devModeUserId) {
        query = query.eq('user_id', devModeUserId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setReceipts(data || []);
      calculateSummary(data || []);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    } finally {
      setLoading(false);
    }
  }

  function calculateSummary(receipts: Receipt[]) {
    const byCategory: Record<string, { amount: number; count: number; deductible: boolean }> = {};
    let total = 0;
    let deductible = 0;
    let taxAmount = 0;
    let eligibleRecords = 0;

    receipts.forEach((receipt) => {
      const amount = receipt.total_amount || 0;
      const categoryName = receipt.categories?.name || 'Uncategorized';
      const isDeductible = receipt.categories?.tax_deductible ?? false;

      total += amount;
      if (isDeductible) {
        deductible += amount;
        eligibleRecords++;
      }

      const receiptTax = receipt.tax_amount || (amount * gstRate);
      taxAmount += receiptTax;

      if (!byCategory[categoryName]) {
        byCategory[categoryName] = { amount: 0, count: 0, deductible: isDeductible };
      }
      byCategory[categoryName].amount += amount;
      byCategory[categoryName].count += 1;
    });

    setSummary({ total, deductible, taxAmount, eligibleRecords, byCategory });
  }

  function exportIRASSummary() {
    if (!summary) return;

    let text = `IRAS TAX SUMMARY\n`;
    text += `Financial Year: ${selectedYear}\n`;
    text += `Generated: ${new Date().toLocaleString()}\n\n`;
    text += `======================================\n\n`;
    text += `Total Verified Business Deductions: $${summary.deductible.toFixed(2)}\n`;
    text += `Total Input Tax (GST): $${summary.taxAmount.toFixed(2)}\n`;
    text += `Eligible Records: ${summary.eligibleRecords}\n`;
    text += `Tax Rate: 9% GST\n\n`;
    text += `======================================\n\n`;
    text += `BREAKDOWN BY CATEGORY\n\n`;

    Object.entries(summary.byCategory)
      .sort(([, a], [, b]) => b.amount - a.amount)
      .forEach(([category, data]) => {
        if (data.deductible) {
          text += `${category}\n`;
          text += `  Amount: $${data.amount.toFixed(2)}\n`;
          text += `  Records: ${data.count}\n\n`;
        }
      });

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iras-summary-${selectedYear}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function exportAuditReport() {
    if (!summary) return;

    const headers = [
      'Date',
      'Merchant',
      'Address',
      'Category',
      'Amount',
      'Currency',
      'Tax Amount',
      'Tax Deductible',
      'Payment Method',
      'Receipt Number',
      'Confidence Score',
      'Status'
    ];

    const rows = receipts.map(receipt => [
      receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : '',
      receipt.merchant_name || '',
      receipt.merchant_address || '',
      receipt.categories?.name || 'Uncategorized',
      (receipt.total_amount || 0).toFixed(2),
      receipt.currency || 'SGD',
      (receipt.tax_amount || 0).toFixed(2),
      receipt.categories?.tax_deductible ? 'Yes' : 'No',
      receipt.payment_method || '',
      receipt.receipt_number || '',
      receipt.confidence_score ? `${(receipt.confidence_score * 100).toFixed(1)}%` : '',
      receipt.status || ''
    ]);

    const csvContent = [
      `# AUDIT REPORT - Financial Year ${selectedYear}`,
      `# Generated: ${new Date().toLocaleString()}`,
      `# Total Records: ${receipts.length}`,
      `# Total Amount: $${summary.total.toFixed(2)}`,
      `# Deductible Amount: $${summary.deductible.toFixed(2)}`,
      `# Total Tax: $${summary.taxAmount.toFixed(2)}`,
      `# Eligible Records: ${summary.eligibleRecords}`,
      '',
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-report-${selectedYear}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const filteredCategories = summary ? Object.entries(summary.byCategory)
    .filter(([category]) =>
      category.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort(([, a], [, b]) => b.amount - a.amount) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('searchVerifiedRecords')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs font-semibold text-brand-primary uppercase tracking-wide">
              {t('fiscalEnvironment')}
            </p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Singapore (SGT)</p>
          </div>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <Bell className="w-5 h-5 text-gray-600" />
          </button>
          <div className="w-10 h-10 bg-brand-primary text-white rounded-lg flex items-center justify-center font-bold">
            LB
          </div>
        </div>
      </div>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{t('taxCenter')}</h1>
          <p className="text-gray-600 dark:text-gray-300 italic">{t('verifiedReportingManaged')}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent font-medium"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>FY {year}</option>
            ))}
          </select>
          <button
            onClick={exportIRASSummary}
            disabled={!summary}
            className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg font-semibold"
          >
            <FileText className="w-4 h-4" />
            {t('exportIRASSummary')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : summary ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-blue-700 via-blue-800 to-blue-900 rounded-3xl p-8 shadow-brand-lg text-white">
                <div className="flex items-center gap-2 mb-6">
                  <Calendar className="w-4 h-4 text-blue-300" />
                  <span className="text-sm font-semibold text-blue-200 uppercase tracking-wide">
                    {t('financialYear')} {selectedYear}
                  </span>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-5xl font-bold">${Math.floor(summary.deductible)}</span>
                    <span className="text-3xl font-bold">.{(summary.deductible % 1).toFixed(2).slice(2)}</span>
                  </div>
                  <p className="text-lg font-medium text-blue-100">
                    {t('totalVerifiedBusinessDeductions')}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-8 pt-6 border-t border-blue-600/30">
                  <div>
                    <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-2">
                      {t('totalInputTax')}
                    </p>
                    <p className="text-2xl font-bold">${summary.taxAmount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-2">
                      {t('eligibleRecords')}
                    </p>
                    <p className="text-2xl font-bold">{summary.eligibleRecords}</p>
                  </div>
                </div>

                <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600/30 border border-blue-400/30 rounded-lg backdrop-blur-sm">
                  <CheckCircle className="w-4 h-4 text-blue-200" />
                  <span className="text-sm font-semibold text-blue-100">{t('complianceVerified')}</span>
                </div>
              </div>
            </div>

            <div className="card space-y-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('filingStatus')}</h3>

              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {t('lytspeedAuditMessage')}
              </p>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                    {t('taxRate')}
                  </span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">9% GST</span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                      {t('dataIntegrity')}
                    </span>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{t('verified').toUpperCase()}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
              </div>

              <button
                onClick={exportAuditReport}
                disabled={!summary}
                className="w-full py-3 px-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
              >
                {t('auditExport')}
              </button>
            </div>
          </div>

          <div className="card">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('taxCategoryDistribution')}</h3>

            {filteredCategories.length > 0 ? (
              <div className="space-y-3">
                {filteredCategories.map(([category, data]) => {
                  const percentage = (data.amount / summary.total) * 100;
                  return (
                    <div key={category} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{category}</span>
                            {data.deductible && (
                              <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 rounded-full font-medium">
                                {t('deductible')}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{data.count} {t('records')}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 dark:text-gray-100 text-lg">${data.amount.toFixed(2)}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}%</p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            data.deductible ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-400 dark:bg-gray-600'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('noCategoriesFound')}
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('verifiedRecords')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-100">{t('date')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-100">{t('merchant')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-100">{t('category')}</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-100">{t('amount')}</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-100">{t('status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {receipts
                    .filter(r => r.categories?.tax_deductible)
                    .map((receipt) => (
                      <tr key={receipt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">
                          {receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 font-medium">
                          {receipt.merchant_name || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {receipt.categories?.name || 'Uncategorized'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right font-semibold">
                          ${(receipt.total_amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-3 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 rounded-full font-medium">
                            {t('verified')}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="card text-center py-12">
          <p className="text-gray-600 dark:text-gray-300">{t('noVerifiedRecordsFound')} {selectedYear}</p>
        </div>
      )}
    </div>
  );
}
